import React, { useState, useEffect } from 'react';
import { Api_Endpoints } from '../../Api/apiEndpoint';
import { makeApiCall } from '../../Api/makeApiCall';
import { Logo } from '../../Constant/ImageConstant';
import { setUserSession } from '../../utils/auth';
import { showErrorToast, showSuccessToast } from '../../utils/toastService';
import './LoginPage.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [emailError, setEmailError] = useState('');
  
  // OTP timer and resend functionality
  const [timer, setTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  
  const MAX_OTP_ATTEMPTS = 3;
  const MAX_LOGIN_ATTEMPTS = 5;
  
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(prevTimer => prevTimer - 1);
      }, 1000);
    } else if (timer === 0 && resendDisabled) {
      setResendDisabled(false);
    }
    
    return () => clearInterval(interval);
  }, [timer, resendDisabled]);
  
  const validateEmail = (value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value) ? '' : 'Please enter a valid email address';
  };
  
  const validatePassword = (value) => {
    if (value.length > 8) {
      return 'Password must not exceed 8 characters';
    }
    
    const hasNumber = /[0-9]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    let error = '';
    
    if (!hasNumber) {
      error += 'Password must contain at least 1 number. ';
    }
    if (!hasUpperCase) {
      error += 'Password must contain at least 1 uppercase letter. ';
    }
    if (!hasLowerCase) {
      error += 'Password must contain at least 1 lowercase letter. ';
    }
    if (!hasSpecialChar) {
      error += 'Password must contain at least 1 special character. ';
    }

    return error;
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setEmailError(validateEmail(newEmail));
  };

  const handlePasswordChange = (e) => {
    const newPasswordValue = e.target.value;
    setPassword(newPasswordValue);
    setPasswordError(validatePassword(newPasswordValue));
  };

  const handleNewPasswordChange = (e) => {
    const newPasswordValue = e.target.value;
    setNewPassword(newPasswordValue);
    setNewPasswordError(validatePassword(newPasswordValue));
  };

  const handleOtpChange = (e) => {
    // Only allow numbers in OTP field
    const value = e.target.value.replace(/[^0-9]/g, '');
    setOtp(value);
  };

  const startOtpTimer = () => {
    setTimer(600); // 10 minutes timer
    setResendDisabled(true);
  };

  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Check if max login attempts exceeded
    if (loginAttempts >= MAX_LOGIN_ATTEMPTS) {
      showErrorToast('Maximum login attempts exceeded. Try again later.');
      return;
    }

    // Validate email
    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      showErrorToast('Please enter a valid email address');
      return;
    }

    // Validate password before proceeding
    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      showErrorToast('Please fix password validation errors');
      return;
    }

    try {
      setIsSubmitting(true);
      setLoginAttempts(prev => prev + 1);
      
      // Call login API to get auth token and OTP
      const response = await makeApiCall(Api_Endpoints.login_Endpoint, 'POST', {
        email: email,
        password: password
      });
      
      if (response?.status) {
        showSuccessToast(response?.message || 'OTP sent to your email/phone');
        // Store auth token for later use
        setAuthToken(response?.result?.auth_token);
        setShowOtpInput(true);
        startOtpTimer();
        setOtpAttempts(0); // Reset OTP attempts when new OTP is sent
      } else {
        showErrorToast(response?.message || 'Invalid login credentials');
      }
    } catch (error) {
      showErrorToast('An error occurred during login');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate OTP
    if (!/^\d{6}$/.test(otp)) {
        showErrorToast('OTP must be 6 digits');
        return;
    }

    // Check if max OTP attempts exceeded
    if (otpAttempts >= MAX_OTP_ATTEMPTS) {
        showErrorToast(`Maximum OTP verification attempts (${MAX_OTP_ATTEMPTS}) exceeded. Please request a new OTP.`);
        return;
    }

    try {
        setIsSubmitting(true);
        setOtpAttempts(prev => prev + 1);

        // Verify OTP using auth token
        const response = await makeApiCall(Api_Endpoints.VERIFY_OTP, 'POST', {
            email: email,
            otp: otp,
            auth_token: authToken
        });

        if (response?.status) {
            showSuccessToast(response?.message || 'OTP verified successfully!');

            // Ensure all necessary user details are present before navigating
            if (response?.result?.token && response?.result?.user) {
                setUserSession({
                    token: response?.result?.token,
                    user: response?.result?.user,
                    email: response?.result?.email,
                    username: response?.result?.username,
                    role: response?.result?.role
                });

                setTimeout(() => {
                    window.location.reload(); // Navigate only when OTP is valid
                }, 500);
            } else {
                showErrorToast("User details are missing. Please try again.");
            }
        } else {
            showErrorToast(response?.message || 'Invalid OTP. Please try again.');
        }
    } catch (error) {
        showErrorToast('An error occurred during OTP verification');
        console.error(error);
    } finally {
        setIsSubmitting(false);
    }
};

const handleForgotPassword = async (e) => {
  e.preventDefault();
  
  if (isSubmitting) return;
  
  // Validate email
  const emailValidationError = validateEmail(email);
  if (emailValidationError) {
    setEmailError(emailValidationError);
    showErrorToast('Please enter a valid email address');
    return;
  }
  
  try {
    setIsSubmitting(true);
    
    // Call forgot password API
    const response = await makeApiCall(Api_Endpoints.FORGET_Password, 'POST', {
      email: email
    });

    if (response?.status) {
      showSuccessToast(response?.message || 'OTP sent to your email/phone');
      setShowOtpInput(true);
      startOtpTimer();
      setOtpAttempts(0); // Reset OTP attempts
      
      // Ensure auth_token is set, with a fallback
      setAuthToken(response?.result?.auth_token || response?.result?.token || '');
    } else {
      showErrorToast(response?.message || 'Failed to send OTP');
    }
  } catch (error) {
    showErrorToast('An error occurred while requesting password reset');
    console.error(error);
  } finally {
    setIsSubmitting(false);
  }
};
const handleResetPassword = async (e) => {
  e.preventDefault();
  
  if (isSubmitting) return;
  
  // Validate OTP
  if (!/^\d{6}$/.test(otp)) {
    showErrorToast('OTP must be 6 digits');
    return;
  }
  
  // Validate new password
  const passwordValidationError = validatePassword(newPassword);
  if (passwordValidationError) {
    showErrorToast('Please fix password validation errors');
    return;
  }
  
  try {
    setIsSubmitting(true);
    
    // First verify the OTP
    const verifyResponse = await makeApiCall(Api_Endpoints.VERIFY_OTP, 'POST', {
      email: email,
      otp: otp,
      auth_token: authToken || '' // Ensure a string is passed
    });
    
    if (!verifyResponse?.status) {
      showErrorToast(verifyResponse?.message || 'Invalid OTP');
      return;
    }
    
    // Determine the auth_token to use for password update
    const tokenToUse = verifyResponse?.result?.token || verifyResponse?.result?.auth_token || authToken || '';
    
    // Call update password API
    const response = await makeApiCall(Api_Endpoints.UPDATE_Password, 'POST', {
      email: email,
      password: newPassword,
      auth_token: tokenToUse
    });

    if (response?.status) {
      showSuccessToast(response?.message || 'Password updated successfully');
      resetForm();
    } else {
      showErrorToast(response?.message || 'Failed to update password');
    }
  } catch (error) {
    showErrorToast('An error occurred while updating password');
    console.error(error);
  } finally {
    setIsSubmitting(false);
  }
};

  const resetForm = () => {
    setForgotPasswordMode(false);
    setShowOtpInput(false);
    setOtp('');
    setNewPassword('');
    setPassword('');
    setAuthToken('');
    setTimer(0);
    setResendDisabled(false);
    setOtpAttempts(0);
  };

  const handleResendOtp = async () => {
    if (isSubmitting || resendDisabled) return;
    
    try {
      setIsSubmitting(true);
      
      // Reset OTP attempts when resending
      setOtpAttempts(0);
      
      // Determine which API to call based on the current mode
      const endpoint = forgotPasswordMode ? Api_Endpoints.FORGET_Password : Api_Endpoints.login_Endpoint;
      const payload = forgotPasswordMode 
        ? { email } 
        : { email, password, auth_token: authToken || '' };
  
      const response = await makeApiCall(Api_Endpoints.RESEND_OTP, 'POST', payload);
      
      if (response?.status) {
        showSuccessToast(response?.message || 'OTP resent successfully');
        // Update auth_token if a new one is provided
        if (response?.result?.auth_token || response?.result?.token) {
          setAuthToken(response.result.auth_token || response.result.token);
        }
        startOtpTimer();
      } else {
        showErrorToast(response?.message || 'Failed to resend OTP');
      }
    } catch (error) {
      showErrorToast('An error occurred while resending OTP');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const renderLoginCredentialsForm = () => (
    <form className="form" onSubmit={handleInitialSubmit}>
      <div className="input-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          name="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="example@gmail.com"
          required
        />
        {emailError && (
          <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
            {emailError}
          </div>
        )}
      </div>
      <div className="input-group">
        <label htmlFor="password">Password</label>
        <input
          type="password"
          name="password"
          value={password}
          onChange={handlePasswordChange}
          placeholder="**********"
          maxLength={8}
          required
        />
        {passwordError && (
          <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
            {passwordError}
          </div>
        )}
      </div>
      
      <button 
        className='loginbutton' 
        type="submit" 
        disabled={isSubmitting || loginAttempts >= MAX_LOGIN_ATTEMPTS}
      >
        {isSubmitting ? 'Processing...' : 'Login'}
      </button>
      
      {loginAttempts >= MAX_LOGIN_ATTEMPTS && (
        <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px', textAlign: 'center' }}>
          Maximum login attempts exceeded. Try again later.
        </div>
      )}
      
      <div className="forgot-password-link" style={{ textAlign: 'center', marginTop: '15px' }}>
        <a href="#" onClick={(e) => { 
          e.preventDefault(); 
          if (!isSubmitting) {
            setForgotPasswordMode(true); 
            setShowOtpInput(false);
            setOtpAttempts(0);
          }
        }}>
          Forgot Password?
        </a>
      </div>
    </form>
  );

  const renderOtpVerificationForm = () => (
    <form className="form" onSubmit={!forgotPasswordMode ? handleOtpSubmit : handleResetPassword}>
      <div className="input-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          name="email"
          value={email}
          readOnly
          disabled
        />
      </div>
      
      {!forgotPasswordMode && (
        <div className="input-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            name="password"
            value="********"
            readOnly
            disabled
          />
        </div>
      )}
      
      {forgotPasswordMode && (
        <div className="input-group">
          <label htmlFor="newPassword">New Password</label>
          <input
            type="password"
            name="newPassword"
            value={newPassword}
            onChange={handleNewPasswordChange}
            placeholder="**********"
            maxLength={8}
            required
          />
          {newPasswordError && (
            <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
              {newPasswordError}
            </div>
          )}
        </div>
      )}
      
      <div className="input-group">
        <label htmlFor="otp">Enter OTP</label>
        <input
          type="text"
          name="otp"
          value={otp}
          onChange={handleOtpChange}
          placeholder="Enter OTP"
          maxLength={6}
          pattern="[0-9]{6}"
          title="OTP must be 6 digits"
          required
        />
        <div style={{ fontSize: '12px', marginTop: '5px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
          <span>Please enter the OTP sent to your email</span>
          <span>Time remaining: {formatTime(timer)}</span>
        </div>
        {otpAttempts > 0 && (
          <div style={{ fontSize: '12px', marginTop: '5px', color: '#666' }}>
            Attempts remaining: {MAX_OTP_ATTEMPTS - otpAttempts} of {MAX_OTP_ATTEMPTS}
          </div>
        )}
      </div>
      
      <button 
        className='loginbutton' 
        type="submit"
        disabled={isSubmitting || otpAttempts >= MAX_OTP_ATTEMPTS}
      >
        {isSubmitting ? 'Processing...' : (!forgotPasswordMode ? 'Verify OTP' : 'Reset Password')}
      </button>
      
      {otpAttempts >= MAX_OTP_ATTEMPTS && (
        <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px', textAlign: 'center' }}>
          Maximum OTP attempts exceeded. Please request a new OTP.
        </div>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
        <a href="#" onClick={(e) => { 
          e.preventDefault(); 
          if (!isSubmitting) {
            setShowOtpInput(false);
            setOtp('');
            setOtpAttempts(0);
          }
        }}>
          Back
        </a>
        
        <a 
          href="#" 
          onClick={(e) => { 
            e.preventDefault();
            if (!resendDisabled && !isSubmitting) {
              handleResendOtp();
            }
          }}
          style={{ 
            color: resendDisabled || isSubmitting ? '#999' : '#0066cc',
            cursor: resendDisabled || isSubmitting ? 'not-allowed' : 'pointer',
            textDecoration: resendDisabled || isSubmitting ? 'none' : 'underline'
          }}
        >
          {resendDisabled ? `Resend OTP (${formatTime(timer)})` : 'Resend OTP'}
        </a>
      </div>
    </form>
  );

  const renderForgotPasswordInitialForm = () => (
    <form className="form" onSubmit={handleForgotPassword}>
      <div className="input-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          name="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="example@gmail.com"
          required
        />
        {emailError && (
          <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
            {emailError}
          </div>
        )}
      </div>
      
      <button 
        className='loginbutton' 
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Processing...' : 'Send OTP'}
      </button>
      
      <div className="back-to-login" style={{ textAlign: 'center', marginTop: '15px' }}>
        <a href="#" onClick={(e) => { 
          e.preventDefault(); 
          if (!isSubmitting) {
            setForgotPasswordMode(false);
            setShowOtpInput(false);
            setEmailError('');
          }
        }}>
          Back to Login
        </a>
      </div>
    </form>
  );

  const renderForm = () => {
    if (forgotPasswordMode) {
      return showOtpInput ? renderOtpVerificationForm() : renderForgotPasswordInitialForm();
    } else {
      return showOtpInput ? renderOtpVerificationForm() : renderLoginCredentialsForm();
    }
  };

  return (
    <div className="login-page">
      <div className="login-page-container">
        <img src={Logo} className="logo" alt="Company LOGO" />
        <h1>
          {forgotPasswordMode 
            ? (showOtpInput ? 'Reset Password' : 'Forgot Password') 
            : (showOtpInput ? 'OTP Verification' : 'Login')}
        </h1>

        {renderForm()}
      </div>
    </div>
  );
};

export default LoginPage;